import { useState, useEffect, useMemo, Suspense } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  AppBar,
  Avatar,
  Box,
  ButtonBase,
  Dialog,
  Divider,
  Drawer,
  IconButton,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import GridViewOutlined from '@mui/icons-material/GridViewOutlined'
import NightlightOutlined from '@mui/icons-material/NightlightOutlined'
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'
import CampaignOutlined from '@mui/icons-material/CampaignOutlined'
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'
import EngineeringOutlined from '@mui/icons-material/EngineeringOutlined'
import LocalFireDepartmentOutlined from '@mui/icons-material/LocalFireDepartmentOutlined'
import PodcastsOutlined from '@mui/icons-material/PodcastsOutlined'
import GroupOutlined from '@mui/icons-material/GroupOutlined'
import AccountBoxOutlined from '@mui/icons-material/AccountBoxOutlined'
import BadgeOutlined from '@mui/icons-material/BadgeOutlined'
import FireTruckOutlined from '@mui/icons-material/FireTruckOutlined'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined'
import AttachMoneyOutlined from '@mui/icons-material/AttachMoneyOutlined'
import WorkHistoryOutlined from '@mui/icons-material/WorkHistoryOutlined'
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined'
import SchoolOutlined from '@mui/icons-material/SchoolOutlined'
import BarChartOutlined from '@mui/icons-material/BarChartOutlined'
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined'
import SettingsOutlined from '@mui/icons-material/SettingsOutlined'
import SearchOutlined from '@mui/icons-material/SearchOutlined'
import LightModeOutlined from '@mui/icons-material/LightModeOutlined'
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined'
import MenuOutlined from '@mui/icons-material/MenuOutlined'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import LogoutOutlined from '@mui/icons-material/LogoutOutlined'
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined'
import SpeedOutlined from '@mui/icons-material/SpeedOutlined'
import SlideshowOutlined from '@mui/icons-material/SlideshowOutlined'

import { logoutUser, selectTenant, refreshSession } from '../store/slices/authSlice'
import { isClerkEnabled } from '../config/clerkConfig'
import { supabase, isAuthEnabled as isSupabaseEnabled } from '../config/supabaseClient'
import { toggleTheme } from '../store/slices/themeSlice'
import { fetchPreferencias } from '../store/slices/preferenciasSlice'
import { prefetchModules } from '../utils/prefetchModules'
import { warmupData } from '../utils/warmupData'
import { NotificationCenter } from './notificaciones'
import ErrorBoundary from './ErrorBoundary'
import useTenantNavigate from '../hooks/useTenantNavigate'
import Tape from './ui/Tape'
import Eyebrow from './ui/Eyebrow'
import CommandPalette from './ui/CommandPalette'
import ToastProvider from './ui/ToastProvider'

const SIDEBAR_WIDTH = 264
const TOPBAR_HEIGHT = 64
const MQ_MOBILE = '(max-width:860px)'

// Módulos del sidebar agrupados (Operación / Recursos / Gestión).
// Se preservan ids, paths, roles y permisos del Layout anterior.
const NAV_ITEMS = [
  // Operación
  { id: 'dashboard', text: 'Sala de Mando', icon: <GridViewOutlined />, path: '/dashboard', roles: ['admin', 'usuario'], grupo: 'Operación' },
  { id: 'guardias', text: 'Guardia Nocturna', icon: <NightlightOutlined />, path: '/guardias', roles: ['admin', 'usuario'], grupo: 'Operación' },
  { id: 'mis-guardias', text: 'Mis Guardias', icon: <NightlightOutlined />, path: '/mis-guardias', roles: ['usuario'], hideIfPermiso: 'guardias', grupo: 'Operación' },
  { id: 'citaciones', text: 'Citaciones', icon: <DescriptionOutlined />, path: '/citaciones', roles: ['admin', 'usuario'], grupo: 'Operación' },
  { id: 'circulares', text: 'Circulares', icon: <CampaignOutlined />, path: '/circulares', roles: ['admin', 'usuario'], grupo: 'Operación' },
  { id: 'cuadro-listas', text: 'Cuadro de Listas', icon: <FactCheckOutlined />, path: '/cuadro-listas', roles: ['admin', 'usuario'], grupo: 'Operación' },
  { id: 'emergencias', text: 'Emergencias', icon: <WarningAmberOutlined />, path: '/emergencias', roles: ['admin', 'usuario'], grupo: 'Operación' },
  { id: 'claves', text: 'Claves Radiales', icon: <PodcastsOutlined />, path: '/claves', roles: ['admin', 'usuario'], grupo: 'Operación' },
  { id: 'compania-servicio', text: 'Compañía en Servicio', icon: <LocalFireDepartmentOutlined />, path: '/compania-servicio', roles: ['admin', 'usuario'], permiso: 'compania-servicio', grupo: 'Operación' },
  // Recursos
  { id: 'bomberos', text: 'Dotación', icon: <GroupOutlined />, path: '/bomberos', roles: ['admin', 'usuario'], grupo: 'Recursos' },
  { id: 'oficiales', text: 'Oficiales', icon: <AccountBoxOutlined />, path: '/oficiales', roles: ['admin', 'usuario'], grupo: 'Recursos' },
  { id: 'tipos-bombero', text: 'Tipos de Bombero', icon: <BadgeOutlined />, path: '/tipos-bombero', roles: ['admin', 'usuario'], permiso: 'tipos-bombero', grupo: 'Recursos' },
  { id: 'material-mayor', text: 'Material Mayor', icon: <FireTruckOutlined />, path: '/material-mayor', roles: ['admin', 'usuario'], grupo: 'Recursos' },
  { id: 'operadores', text: 'Cuarteleros / Maquinistas', icon: <EngineeringOutlined />, path: '/operadores', roles: ['admin', 'usuario'], permiso: 'operadores', grupo: 'Recursos' },
  { id: 'material', text: 'Material Menor', icon: <Inventory2Outlined />, path: '/material', roles: ['admin', 'usuario'], grupo: 'Recursos' },
  // Gestión
  { id: 'cuotas', text: 'Cuotas', icon: <PaymentsOutlined />, path: '/cuotas', roles: ['admin', 'usuario'], permiso: 'cuotas', grupo: 'Gestión' },
  { id: 'mis-cuotas', text: 'Mis Cuotas', icon: <AttachMoneyOutlined />, path: '/mis-cuotas', roles: ['usuario'], grupo: 'Gestión' },
  { id: 'licencias', text: 'Licencias', icon: <WorkHistoryOutlined />, path: '/licencias', roles: ['admin', 'usuario'], grupo: 'Gestión' },
  { id: 'admin-licencias', text: 'Admin Licencias', icon: <AdminPanelSettingsOutlined />, path: '/admin/licencias', roles: ['admin', 'usuario'], permiso: 'licencias.escritura', grupo: 'Gestión' },
  { id: 'especialidades', text: 'Especialidades', icon: <SchoolOutlined />, path: '/especialidades', roles: ['admin', 'usuario'], grupo: 'Gestión' },
  { id: 'estadisticas', text: 'Estadísticas', icon: <BarChartOutlined />, path: '/estadisticas', roles: ['admin', 'usuario'], permiso: 'estadisticas', grupo: 'Gestión' },
  { id: 'notificaciones', text: 'Notificaciones', icon: <NotificationsNoneOutlined />, path: '/notificaciones', roles: ['admin', 'usuario'], grupo: 'Gestión' },
  { id: 'admin-notificaciones', text: 'Config. Notificaciones', icon: <SettingsOutlined />, path: '/admin/notificaciones', roles: ['admin', 'usuario'], permiso: 'notificaciones.escritura', grupo: 'Gestión' },
  { id: 'pantalla', text: 'Pantalla / Mural', icon: <SlideshowOutlined />, path: '/pantalla-config', roles: ['admin'], grupo: 'Gestión' },
  { id: 'admin', text: 'Panel Admin', icon: <AdminPanelSettingsOutlined />, path: '/admin', roles: ['admin'], grupo: 'Gestión' },
]

const ORDEN_GRUPOS = ['Operación', 'Recursos', 'Gestión']

// Breadcrumb y título de topbar por módulo
const TITULOS = {
  dashboard: ['Operación / Tiempo real', 'Sala de Mando'],
  guardias: ['Operación / Turnos', 'Guardia Nocturna'],
  'mis-guardias': ['Operación / Turnos', 'Mis Guardias'],
  citaciones: ['Operación / Convocatoria', 'Citaciones'],
  circulares: ['Operación / Comunicaciones', 'Diario Mural'],
  'cuadro-listas': ['Operación / Asistencia', 'Cuadro de Listas'],
  emergencias: ['Operación / Partes de servicio', 'Emergencias'],
  claves: ['Operación / Comunicaciones', 'Claves Radiales'],
  'compania-servicio': ['Operación / Estado operativo', 'Compañía en Servicio'],
  bomberos: ['Recursos / Personal', 'Dotación de Bomberos'],
  oficiales: ['Recursos / Personal', 'Oficiales'],
  'tipos-bombero': ['Recursos / Personal', 'Tipos de Bombero'],
  'material-mayor': ['Recursos / Flota', 'Material Mayor'],
  operadores: ['Recursos / Flota', 'Cuarteleros / Maquinistas'],
  material: ['Recursos / Equipo', 'Material Menor'],
  cuotas: ['Gestión / Tesorería', 'Control de Cuotas'],
  'mis-cuotas': ['Gestión / Tesorería', 'Mis Cuotas'],
  licencias: ['Gestión / Permisos', 'Licencias'],
  'admin-licencias': ['Gestión / Permisos', 'Admin Licencias'],
  especialidades: ['Gestión / Formación', 'Especialidades'],
  estadisticas: ['Gestión / Análisis', 'Estadísticas'],
  notificaciones: ['Gestión / Avisos', 'Notificaciones'],
  'admin-notificaciones': ['Gestión / Avisos', 'Config. Notificaciones'],
  pantalla: ['Gestión / Difusión', 'Pantalla / Diario Mural'],
  admin: ['Gestión / Administración', 'Panel Admin'],
}

// Atajos g + tecla → ruta (timeout 1200ms)
const ATAJOS_G = {
  d: '/dashboard',
  g: '/guardias',
  c: '/citaciones',
  b: '/bomberos',
  m: '/material-mayor',
  t: '/cuotas',
  e: '/estadisticas',
}

const BOTTOM_NAV = [
  { id: 'dashboard', label: 'Mando' },
  { id: 'guardias', label: 'Guardia' },
  { id: 'bomberos', label: 'Dotación' },
  { id: 'cuotas', label: 'Cuotas' },
]

const AYUDA_ATAJOS = [
  ['General', [['⌘ K', 'Buscar y comandos'], ['?', 'Mostrar esta ayuda'], ['Esc', 'Cerrar diálogos']]],
  ['Ir a módulo', [
    ['G D', 'Sala de Mando'],
    ['G G', 'Guardia Nocturna'],
    ['G C', 'Citaciones'],
    ['G B', 'Dotación'],
    ['G M', 'Material Mayor'],
    ['G T', 'Cuotas'],
    ['G E', 'Estadísticas'],
  ]],
]

// Logo casco (SVG inline de la referencia del rediseño)
const MarcaCasco = () => (
  <Box
    aria-hidden="true"
    sx={(theme) => ({
      width: 38,
      height: 38,
      borderRadius: '10px',
      flex: '0 0 auto',
      position: 'relative',
      display: 'grid',
      placeItems: 'center',
      background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      boxShadow: `0 5px 18px ${alpha(theme.palette.primary.main, 0.3)}`,
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '10px',
        border: '1px solid rgba(255,255,255,0.18)',
      },
    })}
  >
    <svg width="22" height="22" viewBox="0 0 64 64" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 40a20 20 0 0 1 40 0Z" fill="rgba(255,255,255,0.14)" />
      <path d="M8 40h48v3a5 5 0 0 1-5 5H13a5 5 0 0 1-5-5Z" fill="rgba(255,255,255,0.2)" />
      <path d="M28 26h8l-1.5 9h-5Z" fill="#fff" stroke="none" />
    </svg>
  </Box>
)

// Reloj CLT del topbar (componente aparte para aislar el re-render por segundo)
const RelojTopbar = () => {
  const [ahora, setAhora] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  return (
    <Box
      aria-hidden="true"
      sx={(theme) => ({
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.04em',
        fontVariantNumeric: 'tabular-nums',
        display: 'flex',
        alignItems: 'baseline',
        gap: '5px',
        flex: '0 0 auto',
        '@media (max-width: 680px)': { display: 'none' },
      })}
    >
      {ahora.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })}
      <Box component="span" sx={(theme) => ({ fontSize: 10, color: theme.palette.custom.inkDim })}>
        CLT
      </Box>
    </Box>
  )
}

const Layout = () => {
  const isMobile = useMediaQuery(MQ_MOBILE)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [ayudaOpen, setAyudaOpen] = useState(false)

  const navigate = useNavigate()
  const tenantNavigate = useTenantNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { user, demo: esDemo } = useSelector((state) => state.auth)
  const tenant = useSelector(selectTenant)
  const { mode } = useSelector((state) => state.theme)
  const { initialized } = useSelector((state) => state.preferencias)

  const basePath = tenant?.slug ? `/${tenant.slug}` : ''

  // Cargar preferencias del servidor al iniciar (otras páginas dependen de esto)
  useEffect(() => {
    if (user && !initialized) {
      dispatch(fetchPreferencias())
    }
  }, [user, initialized, dispatch])

  // Pre-descargar los chunks de todos los módulos en tiempo ocioso: navegar a
  // un módulo nunca visitado deja de esperar la red (solo carga sus datos).
  useEffect(() => {
    prefetchModules()
  }, [])

  // Precargar también los DATOS de todos los módulos (una vez por sesión):
  // al entrar a cualquier módulo el store ya está poblado y se ve al instante.
  useEffect(() => {
    if (user) warmupData(dispatch)
  }, [user, dispatch])

  // Refresco silencioso de permisos/sesión al montar el shell autenticado: si a un cargo le
  // asignaron un permiso nuevo (ej. emergencias.escritura), se refleja sin re-login. No corre en
  // demo (sesión de solo lectura) y es no-destructivo ante fallos.
  useEffect(() => {
    if (!esDemo) dispatch(refreshSession())
  }, [dispatch, esDemo])

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    handleProfileMenuClose()
    // En demo no hay sesión real que cerrar: volver a la landing.
    if (esDemo) {
      window.location.href = '/'
      return
    }
    // Si Clerk está activo, cerrar TAMBIÉN su sesión. De lo contrario la sesión
    // de Clerk sigue viva y ClerkSessionSync re-hidrata Redux desde
    // /api/clerk/me, re-logueando al usuario automáticamente tras el logout.
    if (isClerkEnabled() && typeof window !== 'undefined' && window.Clerk) {
      try {
        await window.Clerk.signOut()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Logout] Clerk signOut falló', err)
      }
    }
    // Supabase: cerrar la sesión persistida, si no SupabaseSessionSync re-hidrata
    // y el usuario queda re-logueado tras el logout.
    if (isSupabaseEnabled() && supabase) {
      try {
        await supabase.auth.signOut()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[Logout] Supabase signOut falló', err)
      }
    }
    dispatch(logoutUser())
    navigate('/login')
  }

  const handleNavigation = (path) => {
    tenantNavigate(path)
    if (isMobile) {
      setMobileOpen(false)
    }
  }

  // Filtrado por rol + permisos del cargo (misma semántica del Layout anterior)
  const availableNavItems = useMemo(
    () =>
      NAV_ITEMS.filter((item) => {
        if (!item.roles.includes(user?.tipo || 'usuario')) return false
        // Ocultar ítems "self" cuando el usuario YA gestiona el módulo (evita el
        // doble: "Mis Guardias" no se muestra a quien ya ve y edita la planilla
        // completa "Guardia Nocturna"). Se condiciona a la ESCRITURA: un cargo
        // con solo lectura de guardias ve la planilla en modo lectura pero
        // conserva "Mis Guardias" para sus propios turnos/intercambios.
        if (item.hideIfPermiso) {
          const gestionaModulo =
            user?.tipo === 'admin' ||
            (user?.permisos || []).includes(item.hideIfPermiso + '.escritura')
          if (gestionaModulo) return false
        }
        if (item.permiso && user?.tipo !== 'admin') {
          const permisos = user?.permisos || []
          return permisos.some((p) => p === item.permiso || p.startsWith(item.permiso + '.'))
        }
        return true
      }),
    [user]
  )

  const gruposNav = useMemo(
    () =>
      ORDEN_GRUPOS.map((grupo) => ({
        grupo,
        items: availableNavItems.filter((it) => it.grupo === grupo),
      })).filter((g) => g.items.length > 0),
    [availableNavItems]
  )

  // Módulo activo: match más largo del path relativo al tenant
  const relPath = basePath && location.pathname.startsWith(basePath)
    ? location.pathname.slice(basePath.length) || '/'
    : location.pathname
  const activeItem = useMemo(() => {
    const candidatos = NAV_ITEMS.filter(
      (it) => relPath === it.path || relPath.startsWith(it.path + '/')
    )
    candidatos.sort((a, b) => b.path.length - a.path.length)
    return candidatos[0] || null
  }, [relPath])

  const [crumb, tituloPagina] = activeItem
    ? TITULOS[activeItem.id] || [`Cuartel / ${activeItem.grupo}`, activeItem.text]
    : ['Cuartel', tenant?.nombre || 'Cuartel Digital']

  // Items del command palette: módulos disponibles + acciones rápidas
  const paletteItems = useMemo(
    () => [
      ...availableNavItems.map((item) => ({
        id: `mod-${item.id}`,
        label: item.text,
        group: 'Módulos',
        icon: item.icon,
        keywords: [item.id],
        onSelect: () => tenantNavigate(item.path),
      })),
      {
        id: 'accion-nueva-citacion',
        label: 'Nueva citación',
        group: 'Acciones',
        icon: <DescriptionOutlined />,
        keywords: ['crear', 'convocatoria'],
        onSelect: () => tenantNavigate('/citaciones'),
      },
      {
        id: 'accion-registrar-pago',
        label: 'Registrar pago',
        group: 'Acciones',
        icon: <PaymentsOutlined />,
        keywords: ['cuotas', 'tesoreria'],
        onSelect: () => tenantNavigate('/cuotas'),
      },
      {
        id: 'accion-guardia-hoy',
        label: 'Ver guardia de hoy',
        group: 'Acciones',
        icon: <NightlightOutlined />,
        keywords: ['turno', 'guardias'],
        onSelect: () => tenantNavigate('/guardias'),
      },
    ],
    [availableNavItems, tenantNavigate]
  )

  // Atajos globales: ⌘K/Ctrl+K, "?" y chord g+tecla (suprimidos en inputs/modales)
  useEffect(() => {
    let gPendiente = false
    let gTimer = null
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((v) => !v)
        return
      }
      const target = e.target
      const tag = (target?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const modalAbierto = document.querySelector('.MuiModal-root:not([aria-hidden="true"])')
      if (e.key === '?') {
        if (!modalAbierto) {
          e.preventDefault()
          setAyudaOpen(true)
        }
        return
      }
      if (modalAbierto) return
      if (gPendiente) {
        gPendiente = false
        clearTimeout(gTimer)
        const destino = ATAJOS_G[e.key.toLowerCase()]
        if (destino) {
          e.preventDefault()
          tenantNavigate(destino)
        }
        return
      }
      if (e.key.toLowerCase() === 'g') {
        gPendiente = true
        gTimer = setTimeout(() => {
          gPendiente = false
        }, 1200)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      clearTimeout(gTimer)
    }
  }, [tenantNavigate])

  // Un bombero puede tener varios cargos → mostrar todos (fallback al cargo único legacy).
  const nombresCargos = Array.isArray(user?.cargos) && user.cargos.length
    ? user.cargos.map((c) => c?.nombre).filter(Boolean).join(' · ')
    : user?.cargo?.nombre
  const rolUsuario = user?.tipo === 'admin'
    ? 'Administrador del Sistema'
    : nombresCargos || user?.tipoBombero?.nombre || 'Bombero'

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Marca */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '11px',
          padding: '17px 18px 16px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {tenant?.logoUrl ? (
          <Box
            component="img"
            src={tenant.logoUrl}
            alt={tenant?.nombre || 'Logo de la compañía'}
            sx={{ width: 38, height: 38, borderRadius: '10px', objectFit: 'cover', flex: '0 0 auto' }}
          />
        ) : (
          <MarcaCasco />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={(theme) => ({
              fontFamily: theme.typography.fontFamilyDisplay,
              fontSize: 19,
              lineHeight: 0.95,
              letterSpacing: '0.04em',
              color: 'text.primary',
            })}
          >
            CUARTEL{' '}
            <Box component="span" sx={(theme) => ({ color: theme.palette.custom.gold })}>
              DIGITAL
            </Box>
          </Box>
          <Box
            sx={(theme) => ({
              fontFamily: theme.typography.fontFamilyMono,
              fontSize: 9,
              letterSpacing: '0.13em',
              textTransform: 'uppercase',
              color: theme.palette.custom.inkDim,
              marginTop: '3px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            })}
          >
            {tenant?.slug ? `${tenant.slug} · CL` : 'Compañía de Bomberos'}
          </Box>
        </Box>
        {isMobile && (
          <IconButton size="small" onClick={() => setMobileOpen(false)} aria-label="Cerrar menú">
            <CloseOutlined sx={{ fontSize: 19 }} />
          </IconButton>
        )}
      </Box>

      {/* Navegación por grupos */}
      <Box component="nav" aria-label="Navegación principal" sx={{ flex: 1, overflowY: 'auto', padding: '14px 12px 8px' }}>
        {gruposNav.map(({ grupo, items }) => (
          <Box key={grupo} sx={{ marginBottom: '18px' }}>
            <Eyebrow sx={{ fontSize: 9, letterSpacing: '0.2em', padding: '0 10px 8px' }}>{grupo}</Eyebrow>
            {items.map((item) => {
              const activo = activeItem?.id === item.id
              return (
                <ListItemButton
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  aria-current={activo ? 'page' : undefined}
                  sx={(theme) => ({
                    position: 'relative',
                    borderRadius: '9px',
                    padding: '9px 11px',
                    gap: '12px',
                    marginBottom: '2px',
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: activo
                      ? theme.palette.mode === 'dark' ? '#fff' : theme.palette.primary.dark
                      : theme.palette.text.secondary,
                    backgroundColor: activo ? alpha(theme.palette.primary.main, 0.13) : 'transparent',
                    transition: 'color .14s, background-color .14s',
                    '&:hover': {
                      backgroundColor: activo
                        ? alpha(theme.palette.primary.main, 0.13)
                        : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(14,21,36,0.05)',
                      ...(activo ? {} : { color: theme.palette.text.primary }),
                    },
                    '& svg': {
                      fontSize: 19,
                      flex: '0 0 auto',
                      color: activo ? theme.palette.primary.main : 'inherit',
                    },
                    ...(activo && {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -12,
                        top: 7,
                        bottom: 7,
                        width: 3,
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '0 3px 3px 0',
                        boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                    }),
                  })}
                >
                  {item.icon}
                  <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.text}
                  </Box>
                </ListItemButton>
              )
            })}
          </Box>
        ))}
      </Box>

      {/* Usuario conectado */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          padding: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '11px',
        }}
      >
        <ButtonBase
          onClick={handleProfileMenuOpen}
          aria-label="Abrir menú de usuario"
          aria-haspopup="true"
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '11px',
            justifyContent: 'flex-start',
            borderRadius: '9px',
            padding: '4px',
            textAlign: 'left',
          }}
        >
          <Avatar
            sx={{
              width: 38,
              height: 38,
              borderRadius: '9px',
              bgcolor: 'primary.main',
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            {user?.nombre?.charAt(0) || 'U'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.nombre || 'Usuario'}
            </Box>
            <Box
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamilyMono,
                fontSize: 10,
                color: theme.palette.custom.inkDim,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              })}
            >
              {rolUsuario}
            </Box>
          </Box>
        </ButtonBase>
        <IconButton onClick={handleLogout} aria-label="Cerrar sesión" title="Cerrar sesión" size="small">
          <LogoutOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  )

  const bottomItems = BOTTOM_NAV
    .map((b) => {
      const item = availableNavItems.find((it) => it.id === b.id)
      return item ? { ...b, item } : null
    })
    .filter(Boolean)

  return (
    <ToastProvider>
      <Box sx={{ display: 'flex', minHeight: 'var(--cd-full-vh, 100vh)' }}>
        {/* Skip-link accesible al contenido */}
        <Box
          component="a"
          href="#contenido-principal"
          sx={{
            position: 'fixed',
            top: 10,
            left: 10,
            zIndex: 2000,
            transform: 'translateY(-300%)',
            bgcolor: 'primary.main',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: '9px',
            fontWeight: 700,
            fontSize: 13,
            textDecoration: 'none',
            transition: 'transform .15s',
            '&:focus-visible': { transform: 'none' },
          }}
        >
          Saltar al contenido
        </Box>

        {/* Sidebar */}
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
            slotProps: {
              backdrop: {
                sx: { backgroundColor: 'rgba(5,7,11,0.6)', backdropFilter: 'blur(3px)' },
              },
            },
          }}
          sx={(theme) => ({
            width: isMobile ? 0 : SIDEBAR_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: isMobile ? 280 : SIDEBAR_WIDTH,
              maxWidth: isMobile ? '84vw' : 'none',
              boxSizing: 'border-box',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, #0c111b 0%, #090c13 100%)'
                : 'linear-gradient(180deg, #ffffff 0%, #f4f7fb 100%)',
              ...(isMobile && { boxShadow: '18px 0 50px rgba(0,0,0,0.4)' }),
            },
          })}
        >
          {sidebarContent}
        </Drawer>

        {/* Columna principal: topbar + cinta + contenido */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <AppBar position="sticky" elevation={0}>
            <Toolbar
              disableGutters
              sx={{
                minHeight: TOPBAR_HEIGHT,
                height: TOPBAR_HEIGHT,
                gap: '14px',
                px: isMobile ? '14px' : '24px',
              }}
            >
              {isMobile && (
                <IconButton onClick={() => setMobileOpen(true)} aria-label="Abrir menú" edge="start">
                  <MenuOutlined sx={{ fontSize: 19 }} />
                </IconButton>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                {!isMobile && <Eyebrow sx={{ letterSpacing: '0.14em' }}>{crumb}</Eyebrow>}
                <Typography variant="h4" component="h1" noWrap sx={{ fontSize: isMobile ? 15 : 17 }}>
                  {tituloPagina}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              {!isMobile && (
                <ButtonBase
                  focusRipple
                  onClick={() => setPaletteOpen(true)}
                  aria-label="Abrir búsqueda y comandos (atajo Control K)"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    width: 280,
                    padding: '8px 13px',
                    borderRadius: '9px',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    color: 'text.secondary',
                    fontSize: 13,
                    justifyContent: 'flex-start',
                    '@media (max-width: 1000px)': { width: 200 },
                  }}
                >
                  <SearchOutlined sx={{ fontSize: 16 }} />
                  <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>
                    Buscar…
                  </Box>
                  <Box
                    component="kbd"
                    sx={(theme) => ({
                      fontFamily: theme.typography.fontFamilyMono,
                      fontSize: 10,
                      color: theme.palette.custom.inkDim,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '5px',
                      padding: '1px 5px',
                    })}
                  >
                    ⌘K
                  </Box>
                </ButtonBase>
              )}
              <RelojTopbar />
              <Tooltip title={mode === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}>
                <IconButton
                  onClick={() => dispatch(toggleTheme())}
                  aria-label={mode === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
                >
                  {mode === 'light' ? <DarkModeOutlined sx={{ fontSize: 19 }} /> : <LightModeOutlined sx={{ fontSize: 19 }} />}
                </IconButton>
              </Tooltip>
              <NotificationCenter />
              <IconButton
                edge="end"
                onClick={handleProfileMenuOpen}
                aria-label="Cuenta del usuario actual"
                aria-haspopup="true"
              >
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    bgcolor: 'primary.main',
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  {user?.nombre?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </Toolbar>
          </AppBar>
          <Tape height={3} />

          {/* Banner modo demo */}
          {esDemo && (
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                flexWrap: 'wrap',
                padding: '7px 14px',
                backgroundColor: theme.palette.custom.gold,
                color: '#1c1306',
                fontSize: 12.5,
                fontWeight: 700,
              })}
            >
              <Box component="span" sx={{ fontFamily: (t) => t.typography.fontFamilyMono, letterSpacing: '0.1em', fontSize: 11 }}>
                MODO DEMO
              </Box>
              <Box component="span" sx={{ fontWeight: 600 }}>
                Explora libre: los cambios no se guardan ni afectan a nadie.
              </Box>
              <Box
                component="a"
                href="/registro/compania"
                sx={{
                  color: '#1c1306',
                  textDecoration: 'underline',
                  fontWeight: 800,
                  '&:hover': { opacity: 0.8 },
                }}
              >
                Crear mi compañía →
              </Box>
            </Box>
          )}

          {/* Contenido */}
          <Box
            component="main"
            id="contenido-principal"
            tabIndex={-1}
            sx={{
              width: '100%',
              maxWidth: 1500,
              mx: 'auto',
              outline: 'none',
              padding: '26px 28px 60px',
              '@media (max-width: 1100px)': { padding: '20px 18px 50px' },
              '@media (max-width: 860px)': { padding: '18px 15px 96px' },
            }}
          >
            {/* ErrorBoundary por ruta (key=relPath): si una página lanza una
                excepción en runtime, muestra la tarjeta de error recuperable (y
                reporta a Sentry) en vez de desmontar todo a pantalla blanca —el
                bug que dejaba "Mis Guardias" en blanco—. El key la resetea al
                navegar a otra ruta.
                Suspense local: mientras baja el chunk de un módulo lazy solo se
                reemplaza el contenido — sidebar, topbar y banner quedan fijos. */}
            <ErrorBoundary key={relPath}>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                  </Box>
                }
              >
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </Box>
        </Box>

        {/* Menú de usuario (avatar del topbar y bloque del sidebar) */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutOutlined fontSize="small" />
            </ListItemIcon>
            Cerrar Sesión
          </MenuItem>
        </Menu>

        {/* Bottom nav móvil */}
        {isMobile && (
          <Box
            component="nav"
            aria-label="Navegación inferior"
            sx={(theme) => ({
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: theme.zIndex.appBar,
              display: 'flex',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(9,12,19,0.92)' : 'rgba(245,247,251,0.94)',
              backdropFilter: 'blur(16px) saturate(160%)',
              borderTop: `1px solid ${theme.palette.divider}`,
              padding: '6px 6px calc(6px + env(safe-area-inset-bottom))',
            })}
          >
            {bottomItems.map(({ id, label, item }) => {
              const activo = activeItem?.id === id
              return (
                <ButtonBase
                  key={id}
                  onClick={() => handleNavigation(item.path)}
                  aria-current={activo ? 'page' : undefined}
                  aria-label={label}
                  sx={(theme) => ({
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                    padding: '7px 4px',
                    borderRadius: '10px',
                    minHeight: 52,
                    fontSize: 10,
                    fontWeight: 700,
                    color: activo ? theme.palette.primary.main : theme.palette.custom.inkDim,
                    '& svg': { fontSize: 22 },
                  })}
                >
                  {item.icon}
                  <span>{label}</span>
                </ButtonBase>
              )
            })}
            <ButtonBase
              onClick={() => setMobileOpen(true)}
              aria-label="Más módulos"
              sx={(theme) => ({
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                padding: '7px 4px',
                borderRadius: '10px',
                minHeight: 52,
                fontSize: 10,
                fontWeight: 700,
                color: theme.palette.custom.inkDim,
                '& svg': { fontSize: 22 },
              })}
            >
              <MoreHorizOutlined />
              <span>Más</span>
            </ButtonBase>
          </Box>
        )}

        {/* Command palette global (⌘K) */}
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />

        {/* Ayuda de atajos de teclado (?) */}
        <Dialog
          open={ayudaOpen}
          onClose={() => setAyudaOpen(false)}
          maxWidth="xs"
          fullWidth
          aria-label="Atajos de teclado"
        >
          <Tape height={4} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px 18px', borderBottom: '1px solid', borderColor: 'divider' }}>
            <SpeedOutlined sx={(theme) => ({ fontSize: 20, color: theme.palette.custom.gold })} />
            <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700 }}>Atajos de teclado</Typography>
            <IconButton size="small" onClick={() => setAyudaOpen(false)} aria-label="Cerrar">
              <CloseOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box sx={{ padding: '8px 18px 18px' }}>
            {AYUDA_ATAJOS.map(([titulo, atajos]) => (
              <Box key={titulo} sx={{ marginTop: '14px' }}>
                <Eyebrow sx={{ marginBottom: '9px' }}>{titulo}</Eyebrow>
                <Box sx={{ display: 'grid', gap: '8px' }}>
                  {atajos.map(([teclas, descripcion]) => (
                    <Box key={teclas} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box component="span" sx={{ fontSize: 13.5, color: 'text.secondary', fontWeight: 600 }}>
                        {descripcion}
                      </Box>
                      <Box component="span" sx={{ display: 'flex', gap: '4px' }}>
                        {teclas.split(' ').map((tecla) => (
                          <Box
                            key={tecla}
                            component="kbd"
                            sx={(theme) => ({
                              fontFamily: theme.typography.fontFamilyMono,
                              fontSize: 11,
                              minWidth: 22,
                              textAlign: 'center',
                              color: theme.palette.custom.inkDim,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: '6px',
                              padding: '3px 7px',
                            })}
                          >
                            {tecla}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Dialog>
      </Box>
    </ToastProvider>
  )
}

export default Layout
